import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSessionUser } from '../SessionContext';
import { useSoftphone } from '../contexts/SoftphoneContext';
import { logCurrentRoute } from '../hooks/useCurrentRoute';
import { buildAgentStatePayload, sendAgentStateLog } from '../lib/agentStateLog';
import { API_BASE } from '../config/api.js';
import api, { getTokenRole, isTokenValid } from '../services/api.js';
import './Login.css';

// Choose the primary role from the array your API returns
// Example role objects: { ShortCode: "FrontOffice", RoleName: "FO" }
const pickPrimaryRole = (roles = []) => {
  const pref = ['AD', 'BO', 'BOV', 'FOE', 'FO']; // priority order
  const getCode = (r) => String(r?.RoleName || r?.ShortCode || '').toUpperCase(); // "FO", "BO", etc.
  const getLabel = (r) => {
    const raw = r?.ShortCode || r?.RoleLabel || r?.RoleName || '';
    // If raw looks like a short code, use the code as label; otherwise use the human label
    return /^[A-Z]{2,4}$/.test(raw) ? getCode(r) : raw;
  };

  for (const p of pref) {
    const r = roles.find((x) => getCode(x) === p);
    if (r) return { code: p, label: getLabel(r) };
  }
  if (roles[0]) return { code: getCode(roles[0]), label: getLabel(roles[0]) };
  return { code: '', label: '' };
};

const isEnabledFlag = (value) => {
  if (value === undefined || value === null || value === '') return true;
  return !['false', '0', 'no', 'n', 'disabled'].includes(String(value).trim().toLowerCase());
};

const routeForRole = (roleCode = '') => {
  const R = String(roleCode || '').toUpperCase();
  if (R === 'BO' || R === 'BOV') return '/backoffice';
  if (R === 'AD') return '/admin/users';
  return '/frontoffice-new';
};

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useSessionUser();
  const { connect } = useSoftphone();

  const [form, setForm] = useState({ username: '', password: '' });
  const [error, setError]   = useState('');
  const [loading, setLoading] = useState(false);
  const [logoutNotice, setLogoutNotice] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Auto-forward if already logged in with a valid token
  useEffect(() => {
    try {
      const hasToken = !!localStorage.getItem('token');
      const hasUser = !!localStorage.getItem('user');
      logCurrentRoute();
      if (hasToken && hasUser && isTokenValid()) {
        let roleCode = getTokenRole();
        if (!roleCode) {
          const savedUser = JSON.parse(localStorage.getItem('user') || '{}');
          roleCode = savedUser.RoleName || savedUser.roleName || '';
        }
        const targetRoute = routeForRole(roleCode);
        console.log(`🔄 Auto-redirecting to ${targetRoute} (already logged in as ${roleCode || 'unknown'})`);
        navigate(targetRoute, { replace: true });
        return;
      }

      if (hasToken || hasUser) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('extension');
      }
    } catch (err) {
      console.warn('Auto-redirect failed:', err.message);
    }
  }, [navigate]);

  useEffect(() => {
    const notice = location.state?.logoutNotice;
    if (!notice) return;
    setLogoutNotice(String(notice));
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    document.body.classList.add('login-body');
    return () => {
      document.body.classList.remove('login-body');
    };
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  const togglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      let res;
      try {
        res = await axios.post(`${API_BASE}/login`, {
          username: form.username,
          password: form.password,
        });
      } catch (loginError) {
        const responseData = loginError?.response?.data || {};
        const takeoverRequired =
          loginError?.response?.status === 409 &&
          responseData.code === 'SESSION_TAKEOVER_REQUIRED' &&
          responseData.takeoverToken;

        if (!takeoverRequired) throw loginError;

        const confirmed = window.confirm(
          responseData.message ||
          'The previous session is on a call. Continue to end that call and log out the previous session?'
        );
        if (!confirmed) return;

        res = await axios.post(`${API_BASE}/login/takeover`, {
          takeoverToken: responseData.takeoverToken,
        });
      }

      // Backend shape (as per your controller + sample):
      // {
      //   user: {
      //     id, loginName, fullName, departmentId, departmentName,
      //     roles: [{ ShortCode: "FrontOffice", RoleName: "FO" }]
      //   }
      // }
      // Store JWT token
      const token = res?.data?.token || res?.data?.access_token;
      if (token) {
        localStorage.setItem('token', token);
      }

      const apiUser = res?.data?.user || {};
      const rolesArr = Array.isArray(apiUser.roles) ? apiUser.roles : [];

      const userId         = apiUser.UserID ?? apiUser.user_id ?? apiUser.id ?? '';
      const loginName      = apiUser.LoginName ?? apiUser.loginName ?? apiUser.username ?? '';
      const fullName       = apiUser.FullName ?? apiUser.fullName ?? apiUser.name ?? loginName;
      const departmentId   = apiUser.DepartmentId ?? apiUser.departmentId ?? '';
      const departmentName = apiUser.DepartmentName ?? apiUser.departmentName ?? '';

      const { code: roleCode, label: roleLabel } = pickPrimaryRole(rolesArr);

      if (!userId || !fullName) {
        throw new Error('Login succeeded but user payload is missing required fields.');
      }

      // ---- Fetch Extension from tblExtensions ----
      let extensionData = null;
      let loginAgentId = '';
      let softphoneExtension = null;
      try {
        const extRes = await api.get(`/extensions/user/${userId}`);
        extensionData = extRes?.data?.extension || null;
        
        if (extensionData) {
          console.log('✅ Extension found:', extensionData);
          loginAgentId =
            extensionData.ExtensionNumber ||
            extensionData.extension_number ||
            extensionData.extensionNumber ||
            '';
          softphoneExtension = {
            extensionNumber: extensionData.ExtensionNumber || extensionData.extension_number,
            sipUsername: extensionData.SIPUsername || extensionData.sip_username,
            sipPassword: extensionData.SIPPassword || extensionData.sip_password,
            displayName: extensionData.DisplayName || fullName,
            queueName: extensionData.QueueName || extensionData.queueName || extensionData.queuename || '',
            isEnabled: isEnabledFlag(extensionData.IsEnabled ?? extensionData.is_enabled)
          };
          // Store extension info for softphone
          localStorage.setItem('extension', JSON.stringify(softphoneExtension));
        } else {
          console.log('⚠️ No extension found for this user');
        }
      } catch (extErr) {
        console.error('Failed to fetch extension:', extErr);
        // Continue login even if extension fetch fails
      }

      // ---- Source of truth (SessionContext) ----
      setUser({
        userId,
        username: fullName,
        roleName: roleCode,          // e.g., "FO"
        roleLabel,                   // e.g., "FrontOffice"
        loginName,
        departmentId,
        departmentName,
        roles: rolesArr,
      });

      // ---- Single localStorage key for user profile (page refresh recovery) ----
      localStorage.setItem(
          'user',
          JSON.stringify({
            UserID: userId,
            LoginName: loginName,
            FullName: fullName,
            RoleName: roleCode,
            DepartmentId: departmentId,
            DepartmentName: departmentName,
            Roles: rolesArr,
          })
      );

      if (softphoneExtension?.isEnabled) {
        localStorage.setItem('frontOfficeNew_agentAvailability', 'Not Ready');
        connect(softphoneExtension);
      }

      const loginPayload = buildAgentStatePayload({
        status: 'login',
        reason: 'login',
        inQueue: '0',
        agentId: loginAgentId || userId,
        cmsUserId: userId,
        cmsUserName: fullName || loginName
      });
      if (loginPayload) {
        sendAgentStateLog(loginPayload).catch(() => {});
      }

      // ---- Navigate by primary role ----
      const targetRoute = routeForRole(roleCode);
      
      console.log(`🔄 Login successful. Redirecting ${roleCode} to: ${targetRoute}`);
      logCurrentRoute(); // Debug: log before navigation
      navigate(targetRoute);
    } catch (err) {
      const msg =
          err?.response?.data?.error ||
          err?.message ||
          'Login failed';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loginBg = `${process.env.PUBLIC_URL}/login/login-bg.png`;

  return (
    <div className="login-page">
      <main className="screen" style={{ '--login-bg': `url(${loginBg})` }}>
        <section className="main-pane">
          <div className="main-overlay"></div>

          <div className="brand-col">
            <div className="top-row">
              <p className="helpline">24/7 Helpline 0800 70806</p>
            </div>

            <div className="logo-block">
              <h1 className="logo-word">
                SL<span className="logo-a">A</span>CC
              </h1>
              <div className="logo-line"></div>
              <p className="logo-subtitle">Sindh Legal Advisory Call Center</p>
            </div>
          </div>

          <div className="form-col">
            <div className="login-card" role="region" aria-label="Login Panel">
              <h2>Welcome back</h2>
              <p className="signin-sub">Sign in to continue to SLACC Web</p>
              <div className="rule" aria-hidden="true"></div>

              {logoutNotice && (
                <div className="login-notice" role="status">
                  <span>{logoutNotice}</span>
                  <button
                    type="button"
                    className="login-notice-close"
                    aria-label="Close notice"
                    onClick={() => setLogoutNotice('')}
                  >
                    ×
                  </button>
                </div>
              )}

              {error && <div className="login-error">{error}</div>}

              <form className="login-form" onSubmit={handleSubmit}>
                <label className="field-label" htmlFor="username">Username</label>
                <label className="field-wrap">
                  <span className="field-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M3 6.75A1.75 1.75 0 0 1 4.75 5h14.5A1.75 1.75 0 0 1 21 6.75v10.5A1.75 1.75 0 0 1 19.25 19H4.75A1.75 1.75 0 0 1 3 17.25V6.75Zm1.5.43v.07L12 12.38l7.5-5.13v-.07a.25.25 0 0 0-.25-.25H4.75a.25.25 0 0 0-.25.25Zm15 1.9-6.91 4.73a1 1 0 0 1-1.18 0L4.5 9.08v8.17c0 .14.11.25.25.25h14.5a.25.25 0 0 0 .25-.25V9.08Z"/>
                    </svg>
                  </span>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    placeholder="Wajid Mehtab"
                    autoComplete="username"
                    value={form.username}
                    onChange={handleChange}
                  />
                </label>

                <label className="field-label" htmlFor="password">Password</label>
                <label className="field-wrap">
                  <span className="field-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 2a4.5 4.5 0 0 0-4.5 4.5V9H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-1.5V6.5A4.5 4.5 0 0 0 12 2Zm-3 7V6.5a3 3 0 0 1 6 0V9H9Zm3 3a1.75 1.75 0 0 1 1 3.19V17a1 1 0 1 1-2 0v-1.81A1.75 1.75 0 0 1 12 12Z"/>
                    </svg>
                  </span>
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="current-password"
                    value={form.password}
                    onChange={handleChange}
                  />
                  <button
                    className={`eye-btn ${showPassword ? 'is-active' : ''}`}
                    type="button"
                    onClick={togglePassword}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                  >
                    {showPassword ? (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5c4.68 0 8.77 2.98 10.24 7C20.77 16.02 16.68 19 12 19S3.23 16.02 1.76 12C3.23 7.98 7.32 5 12 5Zm0 2C8.1 7 4.68 9.06 3.4 12 4.68 14.94 8.1 17 12 17s7.32-2.06 8.6-5C19.32 9.06 15.9 7 12 7Zm0 1.75A3.25 3.25 0 1 1 8.75 12 3.25 3.25 0 0 1 12 8.75Zm0 2A1.25 1.25 0 1 0 13.25 12 1.25 1.25 0 0 0 12 10.75Z"/>
                      </svg>
                    ) : (
                      <svg viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M12 5c4.68 0 8.77 2.98 10.24 7-.4 1.11-1.02 2.16-1.83 3.1l-1.15-1.16a8.88 8.88 0 0 0 1.35-1.94C19.32 9.06 15.9 7 12 7c-1.3 0-2.54.23-3.67.66L6.76 6.08A11.4 11.4 0 0 1 12 5Zm-9.3.28L4.2 6.8A11.73 11.73 0 0 0 1.76 12c1.47 4.02 5.56 7 10.24 7 1.95 0 3.78-.52 5.35-1.43l1.94 1.94 1.41-1.41-16.58-16.6L2.7 5.28ZM5.73 8.32l1.51 1.51A4.96 4.96 0 0 0 7 11c0 2.76 2.24 5 5 5 .43 0 .85-.05 1.24-.16l1.66 1.66A8.8 8.8 0 0 1 12 18c-3.9 0-7.32-2.06-8.6-5 .47-1.07 1.2-2 2.1-2.68Zm4.28 4.28 2.39 2.39c-.13.01-.27.01-.4.01a3 3 0 0 1-3-3c0-.14 0-.27.01-.4l1 1Z"/>
                      </svg>
                    )}
                  </button>
                </label>

                {/* <div className="auth-row">
                  <label className="remember-wrap">
                    <input type="checkbox" name="remember" value="1" />
                    <span>Remember me</span>
                  </label>
                  <a className="forgot forgot-inline" href="#">Forgot password?</a>
                </div> */}

                <button className="login-btn" type="submit" disabled={loading}>
                  {loading ? 'Signing in…' : 'Sign in'}
                </button>

                {/* <div className="or-row" aria-hidden="true">
                  <span className="or-line"></span>
                  <span className="or-text">or</span>
                  <span className="or-line"></span>
                </div> */}

                {/* <button className="google-btn" type="button">
                  <span className="google-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24">
                      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.3-1.5 3.9-5.5 3.9-3.3 0-6-2.7-6-6s2.7-6 6-6c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.5 14.6 2.6 12 2.6 6.8 2.6 2.6 6.8 2.6 12S6.8 21.4 12 21.4c6.9 0 9.1-4.8 9.1-7.3 0-.5 0-.8-.1-1.2H12Z"/>
                      <path fill="#34A853" d="M3.7 7.9 6.9 10.3c.8-2.3 2.9-3.9 5.1-3.9 1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.5 14.6 2.6 12 2.6c-3.7 0-6.9 2.1-8.3 5.3Z"/>
                      <path fill="#FBBC05" d="M12 21.4c2.5 0 4.7-.8 6.3-2.3l-2.9-2.4c-.8.6-2 1.3-3.4 1.3-2.2 0-4.2-1.5-5-3.6l-3.2 2.5c1.4 3.2 4.6 5.5 8.2 5.5Z"/>
                      <path fill="#4285F4" d="M21.1 12.9c.1-.4.1-.7.1-1.2s0-.8-.1-1.2H12v3.9h5.5c-.3 1.2-1.1 2.2-2.1 2.9l2.9 2.4c1.7-1.6 2.8-4 2.8-6.8Z"/>
                    </svg>
                  </span>
                  <span>Continue with Google</span>
                </button> */}
              </form>

              <p className="card-footer">SLACC WEB 2026 Developed by LAS Team</p>
            </div>
          </div>
        </section>

        <section className="support-strip">
          <div className="support-left">
            <img className="law-dept-logo" src="/login/law-dept-logo.png" alt="Law Department logo" />
          </div>
          <div className="support-right">
            <img className="las-bottom-logo" src="/login/las-logo.png" alt="LAS logo" />
          </div>
        </section>
      </main>
    </div>
  );
}
